/**
 * User Service
 *
 * Service for user CRUD operations with Supabase
 */

import { supabase } from '@/services/supabase/client';
import type { User, UserRow, LanguageCode, CreateUserInput, UpdateProfileInput } from '@/types/user';
import type { Tag } from '@/types/tag';

/**
 * Convert UserRow (snake_case) to User (camelCase)
 */
function rowToUser(row: UserRow): User {
  const user: User = {
    id: row.id,
    talksyId: row.talksy_id,
    name: row.name,
    email: row.email,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };

  if (row.username !== null) {
    user.username = row.username;
  }
  if (row.discriminator !== null) {
    user.discriminator = row.discriminator;
  }
  // Generate tag if both username and discriminator exist
  if (row.username !== null && row.discriminator !== null) {
    user.tag = `${row.username}#${row.discriminator}` as Tag;
  }
  if (row.display_name !== null) {
    user.displayName = row.display_name;
  }
  if (row.avatar_url !== null) {
    user.avatarUrl = row.avatar_url;
  }
  if (row.status_message !== null) {
    user.statusMessage = row.status_message;
  }
  if (row.language !== null) {
    user.language = row.language;
  }
  if (row.timezone !== null) {
    user.timezone = row.timezone;
  }

  return user;
}

/**
 * User service singleton
 */
export const userService = {
  /**
   * Get user by ID
   */
  async getUser(userId: string): Promise<User | null> {
    console.warn('[UserService] getUser called with id:', userId);
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - user doesn't exist
          console.warn('[UserService] User not found in database (PGRST116)');
          return null;
        }
        console.error('[UserService] Failed to get user:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
        throw error;
      }

      console.warn('[UserService] User found in database:', data?.id);
      return rowToUser(data as UserRow);
    } catch (e) {
      console.error('[UserService] Unexpected error in getUser:', e);
      throw e;
    }
  },

  /**
   * Get user by talksy_id (for friend search)
   */
  async getUserByTalksyId(talksyId: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('talksy_id', talksyId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Failed to get user by talksy_id:', error);
      return null;
    }

    return rowToUser(data as UserRow);
  },

  /**
   * Create new user after first Google login
   * talksy_id is auto-generated by database trigger
   */
  async createUser(input: CreateUserInput): Promise<User> {
    console.warn('[UserService] createUser called with id:', input.id);
    
    const { data, error } = await supabase
      .from('users')
      .insert({
        id: input.id,
        name: input.name,
        email: input.email,
        avatar_url: input.avatarUrl ?? null,
        timezone: input.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        // talksy_id is auto-generated by DB trigger
        talksy_id: '', // Will be replaced by trigger
      })
      .select()
      .single();

    if (error) {
      console.error('[UserService] Failed to create user:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
      });
      throw new Error(`Failed to create user: ${error.code} - ${error.message}`);
    }

    console.warn('[UserService] User created in database:', data?.id);
    return rowToUser(data as UserRow);
  },

  /**
   * Update user's language setting
   */
  async updateLanguage(userId: string, language: LanguageCode): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ language })
      .eq('id', userId);

    if (error) {
      console.error('Failed to update language:', error);
      throw new Error(`Failed to update language: ${error.message}`);
    }
  },

  /**
   * Update user profile
   */
  async updateUser(
    userId: string,
    updates: { name?: string; language?: LanguageCode; timezone?: string }
  ): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('Failed to update user:', error);
      throw new Error(`Failed to update user: ${error.message}`);
    }

    return rowToUser(data as UserRow);
  },

  /**
   * Check if user exists
   */
  async userExists(userId: string): Promise<boolean> {
    const { count, error } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('id', userId);

    if (error) {
      console.error('Failed to check user existence:', error);
      return false;
    }

    return (count ?? 0) > 0;
  },

  /**
   * Get user by tag (for friend search)
   */
  async getUserByTag(tag: string): Promise<User | null> {
    const [username, discriminator] = tag.split('#');
    if (!username || !discriminator) {
      return null;
    }

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('username', username)
      .eq('discriminator', discriminator)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Failed to get user by tag:', error);
      return null;
    }

    return rowToUser(data as UserRow);
  },

  /**
   * Search users by username (partial match)
   */
  async searchUsersByUsername(
    query: string,
    limit: number = 20,
    excludeUserId?: string
  ): Promise<User[]> {
    let queryBuilder = supabase
      .from('users')
      .select('*')
      .ilike('username', `%${query}%`)
      .not('username', 'is', null)
      .limit(limit);

    if (excludeUserId) {
      queryBuilder = queryBuilder.neq('id', excludeUserId);
    }

    const { data, error } = await queryBuilder;

    if (error) {
      console.error('Failed to search users:', error);
      throw new Error(`Failed to search users: ${error.message}`);
    }

    return (data || []).map((row) => rowToUser(row as UserRow));
  },

  /**
   * Update user profile (status message, avatar, language)
   */
  async updateProfile(userId: string, updates: UpdateProfileInput): Promise<User> {
    const updateData: Record<string, unknown> = {};

    if (updates.displayName !== undefined) {
      // Validate display name length (max 32 chars)
      if (updates.displayName.length > 32) {
        throw new Error('표시 이름은 32자 이하여야 합니다');
      }
      // Allow empty string to clear display name
      updateData.display_name = updates.displayName || null;
    }
    if (updates.statusMessage !== undefined) {
      updateData.status_message = updates.statusMessage;
    }
    if (updates.avatarUrl !== undefined) {
      updateData.avatar_url = updates.avatarUrl;
    }
    if (updates.language !== undefined) {
      updateData.language = updates.language;
    }
    if (updates.timezone !== undefined) {
      updateData.timezone = updates.timezone;
    }

    const { data, error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('Failed to update profile:', error);
      throw new Error(`Failed to update profile: ${error.message}`);
    }

    return rowToUser(data as UserRow);
  },

  /**
   * Update status message
   */
  async updateStatusMessage(userId: string, statusMessage: string): Promise<void> {
    if (statusMessage.length > 128) {
      throw new Error('상태 메시지는 128자 이하여야 합니다');
    }

    const { error } = await supabase
      .from('users')
      .update({ status_message: statusMessage })
      .eq('id', userId);

    if (error) {
      console.error('Failed to update status message:', error);
      throw new Error(`Failed to update status message: ${error.message}`);
    }
  },

  /**
   * Upload avatar to Supabase Storage
   */
  async uploadAvatar(userId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    // Upload file to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      console.error('Failed to upload avatar:', uploadError);
      throw new Error(`Failed to upload avatar: ${uploadError.message}`);
    }

    // Get public URL
    const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
    const avatarUrl = data.publicUrl;

    // Update user's avatar_url
    const { error: updateError } = await supabase
      .from('users')
      .update({ avatar_url: avatarUrl })
      .eq('id', userId);

    if (updateError) {
      console.error('Failed to update avatar URL:', updateError);
      throw new Error(`Failed to update avatar URL: ${updateError.message}`);
    }

    return avatarUrl;
  },

  /**
   * Check if user needs username setup (onboarding)
   */
  async needsUsernameSetup(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('users')
      .select('username')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Failed to check username setup:', error);
      return false;
    }

    return data.username === null;
  },
};
